import { useCallback, useEffect, useState } from 'react'
import { useStyles } from './ContractListing.styles'
import { useDispatch, useSelector } from '../../store'
import {
  type Contract,
  type ContractsListingState,
} from '../../modules/contractsListing/types'
import {
  getContracts,
  setContracts,
  sendContractsData,
  updateEntitlementsOrDimensions,
} from '../../modules/contractsListing/action'
import cloudStorageLogo from '../../common/images/cloud_storage.svg'
import {
  ButtonV2,
  ContractCardV2,
  LabraToolTip,
  ModalConfirmV2,
  ModalSizeVariants,
  PlanType,
} from '@labrav/react-components'
import { isEmpty } from 'lodash'
import { useNavigate, useParams } from 'react-router-dom'
import {
  isLoading as loading,
  LoadingTypes,
  useCurrentCloudProvider,
  useSyncCloudMarketplace,
  isGivenDateExpired,
  HelpDrawer,
  SnackBar,
  useAccountId,
  setSelectedAccount,
  type CloudType,
} from '@labrav/common'
import { type CloudMarketplace } from '../../modules/productsListing/types'
import { isExpiringInNDays } from '../PrivateOfferForm/ViewPrivateOffer/utils'
import { ListingStatus } from '@labrav/react-components/lib/@types/components/ContractCard'
import { useTimeZone } from '../../common/utils/hooks/useTimeZone'
import { ContractsFooterSubmit } from './ContractsFooterSubmitComponent'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { CircularProgress, Typography } from '@mui/material'
import { faHistory } from '@fortawesome/pro-regular-svg-icons'
import { cmcsExternalPageUrl } from '../../utils/constants'
import { useFlag } from '@labrav/flags'
import { ContractListingV2 } from './ContractListingV2/ContractListingV2'
import { UsageMeteringNoDataState } from '../FlyOutUsageMetering/UsageMeteringNoDataState/UsageMeteringNoDataState'

const getContractListingStatus = (contract: Contract, timeZone: string) => {
  const { contractExpirationDate } = contract
  const isExpiredContract = isGivenDateExpired(contractExpirationDate, timeZone)
  if (contractExpirationDate && isExpiredContract) {
    return 'EXPIRED' as ListingStatus
  } else if (
    contractExpirationDate &&
    isExpiringInNDays({ expirationDate: new Date(contractExpirationDate) })
  ) {
    return 'EXPIRING_SOON' as ListingStatus
  } else {
    return undefined
  }
}

const getContractDataForCard = (
  contract: Contract,
  cloudProvider: CloudMarketplace,
  timeZone: string
) => {
  const {
    id,
    cloudMarketplaceContractId,
    buyerCompanyName,
    buyerLogoUrl,
    contractExpirationDate,
    contractStartDate,
    subscriptionEndDate,
    listingStatus,
    buyerId,
    dimensions,
    entitlements,
    lastMeteringInfoSentAt,
    productType,
    plan,
  } = contract
  let meteringList = []
  const returnContractObj = {
    id,
    cloudMarketplaceContractId: cloudMarketplaceContractId ?? '',
    buyerCompanyName: buyerCompanyName ?? '',
    buyerLogoUrl: buyerLogoUrl ?? '',
    contractExpirationDate: contractExpirationDate ?? '',
    contractStartDate: contractStartDate ?? '',
    subscriptionEndDate: subscriptionEndDate ?? '',
    listingStatus: listingStatus,
    buyerId: buyerId ?? '',
    lastMeteringInfoSentAt: lastMeteringInfoSentAt ?? '',
    productType: productType,
  }
  switch (cloudProvider) {
    case 'AWS': {
      meteringList = entitlements
        ? entitlements.rows
            .filter(
              row =>
                (productType === PlanType.SAAS_CONTRACT &&
                  row.dimensionIsAdditionalUsageAllowed) ||
                productType === PlanType.SAAS_SUBSCRIPTION
            )
            .map(row => ({
              id: row.entitlementId,
              meteringLabel: row.dimensionLabel,
              meteringLabelDescription: row.dimensionLabelDescription,
              unit: row.unit,
              name: row.dimensionName,
              meteringQuantityConsumed: row.meteringQuantityConsumed,
            }))
        : []
      return {
        ...returnContractObj,
        meteringList,
      }
    }
    case 'AZURE': {
      meteringList = dimensions
        ? dimensions?.rows.map(row => ({
            id: row.dimensionId,
            meteringLabel: row.dimensionLabel,
            name: row.dimensionName,
            unit: row.unit,
            meteringQuantityConsumed: row.meteringQuantityConsumed,
          }))
        : []
      return {
        ...returnContractObj,
        listingStatus: getContractListingStatus(contract, timeZone),
        meteringList,
      }
    }
    case 'GCP': {
      meteringList = plan?.dimensions
        ? plan?.dimensions?.map(row => ({
            id: row?.id ?? '',
            meteringLabel: row?.label ?? '',
            unit: row?.unit ?? '',
            name: row?.name ?? '',
            meteringQuantityConsumed: row?.meteringQuantityConsumed,
          }))
        : []
      return {
        ...returnContractObj,
        listingStatus: getContractListingStatus(contract, timeZone),
        meteringList: meteringList,
      }
    }
    case 'REDHAT': {
      return {
        ...returnContractObj,
        meteringList: [],
      }
    }
  }
}

export const ContractListing = () => {
  const { classes } = useStyles()
  const dispatch = useDispatch()
  const [openModal, setOpenModal] = useState<boolean>(false)
  const navigate = useNavigate()
  const { flag: flyoutUsageMeteringEnable } = useFlag('flyoutUsageMetering')
  const { contractsData } = useSelector<ContractsListingState>(
    state => state.contractsListing
  )
  useSyncCloudMarketplace()

  const selectedCloudTypeProductListing = useCurrentCloudProvider()
  const isLoading = useSelector<boolean>(state =>
    loading(state.loading)(LoadingTypes.GENERAL)
  )
  const { timeZone } = useTimeZone()
  const contracts: Contract[] = contractsData.contracts.rows

  const {
    cloud: routeCloud,
    accountId: routeAccountId,
    productId,
  } = useParams<{
    cloud: string
    accountId: string
    productId: string
  }>()
  const fallbackAccountId = useAccountId({ product: 'flyout' }) || ''
  const accountId = routeAccountId || fallbackAccountId
  const { contractId } = useParams<{ contractId: string }>()

  const cloudProvider: CloudMarketplace = routeCloud
    ? (routeCloud.toUpperCase() as CloudMarketplace)
    : selectedCloudTypeProductListing

  const goToSubscriptionsDetail = (subscriptionId: string) => {
    const path =
      routeCloud && accountId
        ? `/flyout/${routeCloud}/${accountId}/subscriptions/${subscriptionId}/detail`
        : `/flyout/subscriptions/${subscriptionId}/detail`
    navigate(path)
  }

  // Sync route params to selectedAccountIds for FLYOUT - single source of truth
  useEffect(() => {
    if (routeAccountId && routeCloud) {
      const routeCloudUpper = routeCloud.toUpperCase() as CloudType
      dispatch(setSelectedAccount(routeAccountId, 'flyout', routeCloudUpper))
    }
  }, [routeAccountId, routeCloud, dispatch])

  useEffect(() => {
    if (accountId) {
      dispatch(getContracts(accountId, productId!, cloudProvider, contractId))
    }
  }, [accountId, productId, contractId, cloudProvider])

  const getKeyNameForUpdateMetering = (cloud: CloudMarketplace) => {
    switch (cloud) {
      case 'AWS': {
        return 'entitlements'
      }
      case 'AZURE': {
        return 'dimensions'
      }
      case 'GCP': {
        return 'plan'
      }
    }
  }

  const handleChange = useCallback(
    async (
      contractId: string,
      meteringId: string,
      values: { meteringQuantityConsumed: string }
    ) => {
      const keyName = getKeyNameForUpdateMetering(cloudProvider)!
      dispatch(
        updateEntitlementsOrDimensions(contractId, {
          meteringId,
          values: values.meteringQuantityConsumed,
          keyName,
        })
      )
    },
    []
  )

  const handleSubmit = useCallback(() => {
    dispatch(sendContractsData(contracts, accountId))
    handleCloseModal()
  }, [contracts, accountId, dispatch])

  const handleCloseModal = useCallback(() => {
    setOpenModal(false)
  }, [])

  const handleOpenModal = useCallback(() => {
    setOpenModal(true)
  }, [])

  const handleClear = useCallback(async () => {
    const inputElements = document.querySelectorAll<HTMLInputElement>(
      'input[type="number"]'
    )
    inputElements.forEach(input => {
      input.value = ''
    })

    const keyName = getKeyNameForUpdateMetering(cloudProvider)!
    const contractData = contracts.map(contract => {
      if (keyName === 'entitlements' && contract.entitlements) {
        return {
          ...contract,
          entitlements: {
            ...contract.entitlements,
            rows: contract.entitlements.rows.map(row => ({
              ...row,
              meteringQuantityConsumed: NaN,
            })),
          },
        }
      } else if (keyName === 'dimensions' && contract.dimensions) {
        return {
          ...contract,
          dimensions: {
            ...contract.dimensions,
            rows: contract.dimensions.rows.map(row => ({
              ...row,
              meteringQuantityConsumed: NaN,
            })),
          },
        }
      } else if (keyName === 'plan' && contract.plan) {
        return {
          ...contract,
          plan: {
            ...contract.plan,
            dimensions: contract.plan.dimensions.map(dimension => ({
              ...dimension,
              meteringQuantityConsumed: NaN,
            })),
          },
        }
      } else {
        return contract
      }
    })

    const updatedData = {
      ...contractsData,
      contracts: {
        rows: [...contractData],
      },
    }
    await dispatch(setContracts(updatedData))
  }, [cloudProvider, contracts, dispatch])

  const handleClickOnBuyerRegistration = (contract: Contract) => {
    window.open(
      `${cmcsExternalPageUrl}/buyer_form?buyer_id=${
        contract?.buyerId
      }&product_name=${
        contract?.productName || 'None'
      }&marketplace_url=None&buyer_exists=False`,
      '_blank'
    )
  }

  return (
    <>
      {isLoading ? (
        <>
          <div className={classes.loaderRootContainer}>
            <img src={cloudStorageLogo} className={classes.logoImg} />
            <Typography
              data-testid="loading-header"
              className={classes.loaderHeader}
            >
              Loading your metering page.
            </Typography>
            <Typography
              data-testid="loading-sub-header"
              className={classes.loaderSubHeader}
            >
              It may take a few moments, please wait...
            </Typography>
            <CircularProgress
              data-testid="circular-progress"
              className={classes.loadingSpinner}
            />
          </div>
        </>
      ) : isEmpty(contracts) && !isLoading ? (
        <>
          <UsageMeteringNoDataState
            text={'No active contracts found'}
            fullHeight={true}
          />
        </>
      ) : flyoutUsageMeteringEnable?.value ? (
        <>
          <ContractListingV2 />
        </>
      ) : (
        <>
          <div
            className={classes.meteringHistoryContainer}
            data-testid="metering-history"
          >
            <LabraToolTip tooltipPlacement="bottom" label="Coming soon">
              <div className={classes.meteringHistoryInnerContainer}>
                <FontAwesomeIcon
                  icon={faHistory}
                  width={'16px'}
                  height={'16px'}
                />
                <Typography className={classes.meteringHeading}>
                  Download metering history
                </Typography>
              </div>
            </LabraToolTip>
          </div>
          <div className={classes.root} data-testid="contract-card-container">
            <div className={classes.contractsContainer}>
              {contracts.map(contract => {
                const contractData = getContractDataForCard(
                  contract,
                  cloudProvider,
                  timeZone
                )
                return (
                  <div key={contract.id} className={classes.contract}>
                    <ContractCardV2
                      contract={contractData}
                      onChange={handleChange}
                      helpAndFaqs={
                        <div className={classes.needMeteringHelp}>
                          <HelpDrawer
                            sectionName="needMeteringHelp"
                            headerTitle="Help"
                          />
                          <ButtonV2
                            smallVariant
                            styleType="outlined"
                            onClick={() =>
                              handleClickOnBuyerRegistration(contract)
                            }
                            data-testid="add-a-user-button"
                          >
                            Buyer registration form
                          </ButtonV2>
                        </div>
                      }
                      handleAction={() =>
                        contract.id && goToSubscriptionsDetail(contract.id)
                      }
                    />
                  </div>
                )
              })}
            </div>
          </div>
          <div data-testid="contracts-footer-wrapper">
            <ContractsFooterSubmit
              onSubmit={handleOpenModal}
              onClear={handleClear}
              submitBtnTitle="Submit metering information"
              key="footer-submit"
            />
            <ModalConfirmV2
              title={'Confirm Metering Records'}
              content={
                <>
                  Sending this metering will be final and can't be reverted!{' '}
                  <br /> Status will be sent on mail upon confirmation from{' '}
                  {cloudProvider} marketplace.
                </>
              }
              open={openModal}
              titleAcceptButton={'Confirm send'}
              onAccept={handleSubmit}
              onDecline={handleCloseModal}
              variant={ModalSizeVariants.large}
              titleCancelButton={'Return to edit'}
            />
          </div>
          <SnackBar />
        </>
      )}
    </>
  )
}
